import { type JSX } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';

/**
 * Wraps every protected route. While the initial session check is still
 * running, render a centered spinner — flashing the login screen and bouncing
 * back is worse UX than a 100ms spinner. Once the check resolves:
 *  - authenticated → render the children (`<Outlet />` in the route tree)
 *  - anonymous     → redirect to /login, preserving the original `from`
 *                    location so the post-login navigation lands the user
 *                    where they were headed.
 */
export function ProtectedRoute(): JSX.Element {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const { t } = useTranslation('default');

  if (isLoading) {
    return (
      <div
        className="min-h-screen w-full grid place-items-center bg-paper"
        role="status"
        aria-live="polite"
      >
        <div className="flex flex-col items-center gap-3 text-text-3">
          <Spinner className="w-5 h-5" />
          <span className="text-sm">{t('app_loading')}</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}
